import Button from '@rio-cloud/rio-uikit/Button';
import { useId } from 'react';
import { useIntl } from 'react-intl';

import { CustomDateRangePicker } from '#components/shared/CustomDateRangePicker.tsx';
import { Filter } from '#components/shared/Filter.tsx';
import { SearchInput } from '#components/shared/SearchInput.tsx';

import type { useOrderListFilters } from '../hooks/useOrderListFilters.ts';

const filterMessageIds = {
    title: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.title',
    search: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.search',
    type: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.type',
    reset: 'intl-msg:ordercom-cloud-frontend.tours.order-filters.reset',
};
const visuallyHiddenLabelStyle = {
    position: 'absolute' as const,
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap' as const,
    border: 0,
};

export const OrderListFilters = ({ filters }: { filters: ReturnType<typeof useOrderListFilters> }) => {
    const intl = useIntl();
    const id = useId();
    const message = (key: keyof typeof filterMessageIds) => intl.formatMessage({ id: filterMessageIds[key] });
    return (
        <fieldset
            className='display-flex flex-column gap-10 margin-bottom-10'
            style={{ flexShrink: 0 }}
            aria-label={message('title')}
        >
            <div className='display-flex align-items-center justify-content-end gap-10'>
                <div style={{ flex: '0 0 100px' }}>
                    <label style={visuallyHiddenLabelStyle} htmlFor={`${id}-type`}>
                        {message('type')}
                    </label>
                    <Filter
                        showUnselectedItemIcons={false}
                        className='width-100pct'
                        pullRight={false}
                        id={`${id}-type`}
                        placeholder={message('type')}
                        values={filters.types}
                        onFilter={filters.setTypes}
                        options={['transport', 'service'].map(type => ({
                            id: type,
                            icon: <span className={`uikit-icon icon-${type === 'transport' ? 'truck' : 'wrench'}`} />,
                            label: intl.formatMessage({
                                id: `intl-msg:ordercom-cloud-frontend.order.type.${type}`,
                            }),
                        }))}
                    />
                </div>
                <div className='flex-1-1 min-width-0'>
                    <CustomDateRangePicker
                        id={`${id}-range`}
                        period={filters.period}
                        setPeriod={filters.setPeriod}
                        value={filters.selectedDates}
                        onChange={filters.setCustomDatesRange}
                    />
                </div>
                <Button
                    bsStyle={Button.MUTED}
                    type='button'
                    onClick={filters.reset}
                    iconName='filter-active'
                    iconOnly
                    style={{ flexShrink: 0 }}
                    aria-label={message('reset')}
                    title={message('reset')}
                />
            </div>
            <label style={visuallyHiddenLabelStyle} htmlFor={`${id}-search`}>
                {message('search')}
            </label>
            <SearchInput
                id={`${id}-search`}
                value={filters.searchInput}
                onSearch={filters.setSearchInput}
                placeholder={message('search')}
            />
        </fieldset>
    );
};
